import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Trash } from 'lucide-react'

import { ClientOrdersTable } from './ClientOrdersTable'
import { ClientRunaHistoryTable } from './ClientRunaHistoryTable'
import { UserInfoForm } from './UserInfoForm'
import {
  BillingAddressForm,
  ShippingAddressForm,
  type IBillingValues,
  type IShippingValues,
} from './AddressForm'
import Loader from '@/common/components/Loader'
import { useCheckPermission } from '@/core/hooks/use-check-permission'
import { type IClient, type IClientPayload } from '@/core/services/clients'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { useClients } from '@/home/hooks/use-clients'
import { Button, Card, Input } from '@/components/ui'
import { Header, PageTitle } from '@/components'
import FormGroup from '@/core/components/FormGroup'
import { Icon } from '@/common/components/Icon'

export const ClientsDetailsPage: React.FC = (): JSX.Element => {
  const { clientId } = useParams()
  const navigate = useNavigate()
  const [client, setClient] = useState<IClient>()
  const [billingAddress, setBillingAddress] =
    useState<Partial<IBillingValues>>()
  const [shippingAddress, setShippingAddress] =
    useState<Partial<IShippingValues>>()
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const { hasEditPermission } = useCheckPermission({ page: 'CLIENTS' })
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const {
    fetchClientDetails,
    fetchClientAddresses,
    updateClientDetails,
    updateBillingAddress,
    updateShippingAddress,
    sendConfirmationEmail,
    resetRedeem,
    loading,
  } = useClients()

  const getData = useCallback(async () => {
    if (!clientId) return
    try {
      const data = await fetchClientDetails(clientId)
      setClient(data)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }, [clientId, fetchClientDetails])

  const getAddresses = useCallback(async () => {
    if (!clientId) return
    try {
      const { shipping, billing } = await fetchClientAddresses(clientId)

      setBillingAddress({
        id: billing.id,
        address: billing.address,
        city: billing.city,
        country: billing.country,
        nationality: billing.nationality,
        postCode: billing.postCode,
      })

      if (shipping.useDataFromBillingAddress) {
        setShippingAddress({
          id: billing.id,
          address: billing.address,
          city: billing.city,
          country: billing.country,
          postCode: billing.postCode,
          sameAsBilling: true,
        })
      } else {
        setShippingAddress({
          id: shipping.id,
          address: shipping.address,
          city: shipping.city,
          country: shipping.country,
          postCode: shipping.postCode,
          sameAsBilling: false,
        })
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }, [clientId, fetchClientAddresses])

  useEffect(() => {
    getData()
    getAddresses()
  }, [getData, getAddresses])

  const handleSendEmail = async () => {
    if (!hasEditPermission || !clientId || !client) return
    try {
      await sendConfirmationEmail(clientId, client.email)
      setIsError(false)
      setShowMessageBox(true)
      setMessage('Email sent.')
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const handleDelete = () => {
    if (!hasEditPermission) {
      return
    }
    setDeleteConfirmationModal(true)
  }

  const handleResetRedeemCounter = async () => {
    if (!hasEditPermission || !clientId) {
      return
    }
    try {
      await resetRedeem(clientId)
      setIsError(false)
      setShowMessageBox(true)
      setMessage('Reset Done!')
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const deleteFunc = () => {
    ;(async () => {
      try {
        console.log('deleted')
        // TODO: Implement delete functionality
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus(
            (e as { response?: { status?: number } })?.response?.status,
          )
        } else {
          console.error(e)
        }
      }
    })()
  }

  const handleUserInfoSubmit = async (payload: IClientPayload) => {
    if (!hasEditPermission || !clientId) {
      return
    }
    try {
      await updateClientDetails(clientId, payload)
      setIsError(false)
      setShowMessageBox(true)
      setMessage('Your changes have been saved.')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      if (e.response?.data?.status === 400) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(
          'In order to change the phone number, the check-in must be canceled first.',
        )
      } else {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus(
            (e as { response?: { status?: number } })?.response?.status,
          )
        } else {
          console.error(e)
        }
      }
    }
  }

  const handleBillingAddressSubmit = async (data: IBillingValues) => {
    if (!hasEditPermission || !clientId) {
      return
    }
    try {
      await updateBillingAddress(clientId, {
        id: data.id || '',
        address: data.address,
        country: data.country,
        city: data.city,
        postCode: data.postCode,
        nationality: data.nationality,
      })
      setIsError(false)
      setShowMessageBox(true)
      setMessage('Your changes have been saved.')
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const handleShippingAddressSubmit = async (data: IShippingValues) => {
    if (!hasEditPermission || !clientId) {
      return
    }
    try {
      await updateShippingAddress(clientId, {
        id: data.id || '',
        address: data.address,
        country: data.country,
        city: data.city,
        postCode: data.postCode,
        useDataFromBillingAddress: data.sameAsBilling,
      })
      setIsError(false)
      setShowMessageBox(true)
      setMessage('Your changes have been saved.')
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    }
  }

  const { festivalName, editionId } = useParams()

  return (
    <>
      <Header
        pageTitle="Client Details"
        actions={
          <>
            {hasEditPermission && (
              <>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={handleResetRedeemCounter}
                >
                  Reset Redeem Counter
                </Button>
                <Button size="sm" variant="destructive" onClick={handleDelete}>
                  Delete User
                  <Icon
                    icon="close-circle"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                  />
                </Button>
              </>
            )}
          </>
        }
      />
      {loading && <Loader />}
      <div className="overflow-y-auto max-h-[calc(100vh-150px)]">
        <PageTitle
          title={`${client?.firstName || ''} ${client?.lastName || ''}`}
          backButton
          hidePageIcon
          backButtonFunction={() =>
            navigate(`/clients/${festivalName}/${editionId}`)
          }
        />

        <div className="mt-8 space-y-8">
          {/* User Info Section */}
          <Card className="p-[30px] bg-monochrome-4-60">
            <h3 className="text-xl font-semibold text-monochrome-1 mb-4">
              User Info
            </h3>
            <UserInfoForm
              client={client}
              hasEditPermission={hasEditPermission}
              onSubmit={handleUserInfoSubmit}
              onSendEmail={handleSendEmail}
            />
          </Card>

          {/* Address Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <BillingAddressForm
              initialData={billingAddress}
              hasEditPermission={hasEditPermission}
              onSubmit={handleBillingAddressSubmit}
            />
            <ShippingAddressForm
              initialData={shippingAddress}
              billingData={billingAddress}
              hasEditPermission={hasEditPermission}
              onSubmit={handleShippingAddressSubmit}
            />
          </div>

          {/* Orders Section */}
          {clientId && (
            <Card className="px-[30px] py-5 bg-monochrome-4-60">
              <PageTitle title="Orders" className="mb-5" />

              <ClientOrdersTable clientId={clientId} maxHeight="auto" />
            </Card>
          )}

          {/* RUNA Section */}
          <Card className="px-[30px] py-5 bg-monochrome-4-60">
            <h3 className="text-xl font-semibold text-monochrome-1 mb-4">
              RHUNA
            </h3>
            <FormGroup label="Current Amount">
              <Input type="text" placeholder="Current amount" />
            </FormGroup>
          </Card>

          {clientId && (
            <Card className="px-[30px] py-5 bg-monochrome-4-60">
              <ClientRunaHistoryTable clientId={clientId} />
            </Card>
          )}
        </div>
      </div>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Client'}
        deleteFunc={deleteFunc}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
